import styles from "./index.module.css";
import BoxContent from "../BoxContent";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import Project from "./Project";
import lightLogo from "./logolong.png";
import darkLogo from "./OLDlogolong.png";
import { ThemeContext } from "../../context/ThemeContext";
import { auth } from "../../firebase";
import { authenticatedFetch } from "../../services/apiClient";

export default function Home(props) {
  const [productMetaData, setProductMetaData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPdfs, setIsLoadingPdfs] = useState(true);
  const [pdfs, setPdfs] = useState([]);
  const navigate = useNavigate();
  const [showMoreSections, setShowMoreSections] = useState({});
  const { isDarkMode } = useContext(ThemeContext);
  const [theme, setTheme] = useState(isDarkMode);
  const [logo, setLogo] = useState(lightLogo);

  let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const toggleShowMore = (index) => {
    setShowMoreSections((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

const signout = async () => {
  try {
    console.log("🚪 Logout initiated...");

    // Step 1: Revoke all refresh tokens on all devices (logout everywhere)
    try {
      await authenticatedFetch(`${BACKEND_URL}/api/auth/logout-everywhere`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      console.log("✅ All sessions invalidated on backend");
    } catch (logoutError) {
      // Log but continue - don't let logout-everywhere failure prevent local logout
      console.warn("⚠️ Failed to invalidate all sessions (continuing with local logout):", logoutError);
    }

    // Step 2: Sign out from Firebase (clears session)
    await auth.signOut();
    console.log("✅ Signed out from Firebase");

    // Step 3: Redirect to login
    navigate("/login");
  } catch (err) {
    console.error("❌ Logout error:", err);
    // Force redirect even on error
    navigate("/login");
  }
};


  useEffect(() => {
    setTheme(isDarkMode);
    setLogo(isDarkMode ? darkLogo : lightLogo);

    // Since ProtectedRoute already ensures user is authenticated,
    // just fetch the data directly
    async function fetchProjects() {
      try {
        // Use authenticatedFetch helper (handles token + 401 automatically)
        const res = await authenticatedFetch(
          `${BACKEND_URL}/api/project/get_projects`
        );

        const json = await res.json();

        if (json && json.data) {
          setProductMetaData(json.data);
        } else {
          setProductMetaData([]);
        }
      } catch (err) {
        console.error("API error:", err);
        setProductMetaData([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProjects();

    async function fetchPdfs() {
      try {
        setIsLoadingPdfs(true);
        const res = await authenticatedFetch(`${BACKEND_URL}/api/project/results`);

        if (!res.ok) {
          throw new Error(`Results request failed with status ${res.status}`);
        }

        const json = await res.json();
        const items = Array.isArray(json?.data) ? json.data : [];

        const mapped = items.map((item) => ({
          name: item?.name || "report",
          download: item?.downloadUrl,
          csvDownload: item?.csvDownloadUrl || null,
          rawDataDownload: item?.rawDataDownloadUrl || null,
        }));

        setPdfs(mapped);
      } catch (error) {
        console.error("Error fetching PDFs:", error);
        setPdfs([]);
      } finally {
        setIsLoadingPdfs(false);
      }
    }

    fetchPdfs();
  }, [BACKEND_URL, isDarkMode, navigate]);

  const textColorLightMode = "black";
  const textColorDarkMode = "white";
  const backgroundColorLightMode = "white";
  const backgroundColorDarkMode = "black";
  const DownloadTextColorLightMode = "#007bff";
  const DownloadTextColorDarktMode = "#007bff";

  return (
    <div>
      <header>
        <button
          onClick={() => navigate("/manageUser")}
          className="create-product"
        >
          Settings
        </button>
        <img
          src={logo} 
          alt="Linesol"
          style={{ height: "44px" }}
        />
        <button className="sign-out" onClick={signout}>
          Sign Out
        </button>
      </header>

      <BoxContent>
        <h2>
          <u>
            <strong>Launch Studies:</strong>
          </u>
        </h2>
        <div className={styles["launchExperimentText"]}>
          <p>
            Linesol helps teams test pre-launch decisions through controlled binary experiments. Each participant sees one question card built from fields such as title, image, description, and price, so product concepts, pricing, messaging, visuals, and audience variables can be tested under controlled conditions. Once a study completes, an automated ML analysis pipeline evaluates those responses alongside the participant attributes selected for the study and returns interpretable outputs teams can act on. If you are evaluating fit, start with our <a href="https://dashboard.linesol.online/client-study-guide" style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}>supportive guide</a> and the case studies at <a href="https://linesol.ai/#casestudies" style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}>linesol.ai/#casestudies</a>.
          </p>
          <br />
          <p>
            Before launch, teams can review available participant demographics during study setup. Once a study completes, the results include raw data, a structured results file, and a detailed report covering feature effects, pricing signals, segment differences, model accuracy scores, and follow-up study recommendations. For teams that want to run the same workflow programmatically, the free <a href="https://dashboard.linesol.online/createApiKey" style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}>API</a> can be used to check demographic coverage, launch studies, and retrieve results.
          </p>
          <br />
          <p>
            Pricing is usage-based: Pay per response at $0.10 each. 
          </p>
          <br />
          <p>
            <u>
              <strong>What You Get Back:</strong>
            </u>
          </p>
          <br />
          <div className={styles["keyFindings"]}>
            <p>
              1.{" "}
              <u>
                <strong>Feature Effects</strong>
              </u>
              : See which card variables and participant attributes carry the strongest predictive importance for positive response rates.
              <span
                style={{
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  marginLeft: "5px",
                }}
                onClick={() => toggleShowMore(1)}
              >
                {showMoreSections[1] ? "Show Less" : "Show More"}
              </span>
            </p>
            {showMoreSections[1] && (
              <div style={{ marginLeft: "20px" }}>
                <p>
                  - A ranked list of feature importances is obtained through the
                  genetic optimization of Ridge Regression and Random Forest models,
                  generating and evolving 500 targeted hyperparameter
                  configurations to maximise the predicitve accuracy of the
                  models. Features that show consistent importance across top
                  performing models form clear, stable patterns which can be
                  interpreted as key predictors of positive response rates and
                  are paired with their Pearson correlation coefficients to show
                  net positive or negative association with responses.
                </p>
                <p>
                  - By comparing the results from Ridge Regression and Random
                  Forest models, we can determine if the underlying patterns in
                  the data are mainly linear (Ridge Regression) or involve non-linear
                  relationships and interactions (Random Forest). This is important because it helps us understand if the variables function as stable additive effects (linear interactions) or as context-dependent combinations across other question-card and user-feature permutations (non-linear interactions). Random Forest models often perform well on linear data but rarely outperform the Ridge model unless significant non-linear interactions are present.
                </p>
                <p>
                  - Only users with traits that are specified in the user
                  features of the study are permitted to participate, ensuring
                  the data accurately reflects the target demographic.
                </p>
              </div>
            )}
            <br />

            <p>
              2.{" "}
              <u>
                <strong>Pricing Signals</strong>
              </u>
              : Compare revenue per product view across the price points tested in this study, based on observed response rates.
              <span
                style={{
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  marginLeft: "5px",
                }}
                onClick={() => toggleShowMore(2)}
              >
                {showMoreSections[2] ? "Show Less" : "Show More"}
              </span>
            </p>
            {showMoreSections[2] && (
              <div style={{ marginLeft: "20px" }}>
                <p>
                  - When price is included as a tested field, Linesol estimates how response rates differ across the price points you supplied.
                </p>
                <p>
                  - This helps teams compare revenue per product view and pricing tradeoffs across the tested conditions before committing to a pricing direction.
                </p>
                <p>
                  - Calculated from observed positive response rates at various
                  price points.
                </p>
              </div>
            )}

            <br />

             <p>
              3.{" "}
              <u>
                <strong>Suggested Improvements</strong>
              </u>
              : Review multicollinearity results and apply dimensionality-reduction techniques to refine feature selection, improving model stability, interpretability, and predictive accuracy.
              <span
                style={{
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  marginLeft: "5px",
                }}
                onClick={() => toggleShowMore(4)}
              >
                {showMoreSections[4] ? "Show Less" : "Show More"}
              </span>
            </p>
            {showMoreSections[4] && (
              <>
                <br />
                <p style={{ marginLeft: "20px" }}>
                  Multicollinearity and redundant predictors are common challenges in predictive modeling that can reduce the stability, interpretability, and generalization performance of machine-learning models. When multiple features capture similar underlying constructs, models may struggle to differentiate their individual contributions, resulting in feature importance scores that fluctuate across runs, datasets, or minor hyperparameter changes. Similarly, irrelevant or redundant variables introduce noise and unnecessary complexity without improving predictive signal.
                </p>
                <br />
                <p style={{ marginLeft: "20px" }}>
                  To support deeper analysis, the platform now provides clients with the raw one-hot encoded dataset used in the modeling pipeline, alongside the correlation matrix. This allows researchers to perform their own dimensionality-reduction or feature-engineering procedures, such as Multiple Correspondence Analysis, to identify latent constructs and consolidate correlated predictors into composite variables.
                </p>
                <br />
                <p style={{ marginLeft: "20px" }}>
                  {" "}
                  If meaningful composite variables are derived (for example, latent variables representing underlying user preferences), they can be submitted to hello@linesol.online for review and, if validated, may be added as selectable user features for future studies. This enables studies to progressively shift from many closely related indicators toward fewer, higher-level variables that better capture key predictors of positive response rates.
                </p>
                <br />
                <p style={{ marginLeft: "20px" }}>
                  {" "}                  
                  Although regularized linear models such as Ridge Regression and ensemble methods like Random Forest already mitigate some effects of correlated predictors, sustained improvements in stability and interpretability typically require systematic feature refinement and, where possible, larger datasets.
                </p>
                <br />
                <p style={{ marginLeft: "20px" }}>
                  Strategies for the Iterative Improvement Cycle:
                </p>
                <ul
                  style={{
                    color: theme ? textColorDarkMode : textColorLightMode,
                    marginLeft: "50px",
                  }}
                >
                  <li>
                    <strong>Introduce New Features:</strong> Develop and test new predictors that capture underlying psychological, behavioral, or contextual predictors of the outcome. This can help replace highly correlated variables and support the development of a more nuanced and informative model.
                  </li>
                  <li>
                    <strong>Replace Highly Correlated Features:</strong> Consolidate clusters of highly correlated predictors into a single feature or composite variable grounded in theoretical or empirical justification using dimensionality-reduction techniques (e.g., Multiple Correspondence Analysis).
                  </li>
                  <li>
                    <strong>Remove Redundant Features:</strong> Remove variables that contribute to multicollinearity without meaningfully improving predictive power, thereby improving model efficiency and interpretability.
                  </li>
                  <li>
                    <strong>Hierarchical Strategy:</strong> Introduce predictors in stages across multiple studies, beginning with variables that are theoretically central to the outcome and progressively expanding the feature set.
                  </li>
                  <li>
                    <strong>Expand Sample Size:</strong> Increasing the number of observations provides more stable coefficient estimates and reduces the influence of multicollinearity.
                  </li>
                </ul>
              </>
            )}
            <br />
          </div>
          <p>
           Linesol is designed to help identify winning product design, audience and price combinations through clear results and study diagnosics. It helps reduce uncertainty before committing manufacturing or marketing decisions. 
          </p>
        </div>
        <div className={styles["launchExperiment"]}>
          <button onClick={() => navigate("/form1")}>Launch Study</button>
        </div>

        <h2>
          <u>Current Studies:</u>
        </h2>
        <div className={styles["table-container"]}>
          <table className={styles["home-products-table"]}>
            <thead>
              <tr>
                <th>Study Name</th>
                <th>Start Date</th>
                <th>Current Decisions</th>
                <th>Max Decisions</th>
                <th>Question</th>
                <th>Question Features</th>
                <th>User Features</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                productMetaData
                  .filter((product) => product.purchaseConfirmed === 'Yes')
                  .map((product) => (
                    <Project
                      key={product.id}
                      productId={product.id}
                      currentDecisions={product.currentDecisions}
                      maxDecisions={product.maxDecisions}
                      startDate={product.startDate}
                      projectName={product.projectName}
                      question={product.permutations[0]?.question}
                    />
                  ))
              )}
            </tbody>
          </table>
        </div>
        <h2>
          <u>Study Results:</u>
        </h2>
        <div className={styles["table-container"]}>
          <table className={styles["home-products-table"]}>
            <thead>
              <tr>
                <th>Study Name</th>
                <th>Raw Data CSV</th>
                <th>Results CSV</th>
                <th>Report PDF</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPdfs ? (
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                pdfs.length > 0 &&
                  pdfs.map((pdf) => (
                    <tr
                      key={pdf.name}
                      style={{
                        background: theme
                          ? backgroundColorDarkMode
                          : backgroundColorLightMode,
                        borderColor: theme
                          ? "--dark-white-color"
                          : "--dark-white-color-lights",
                      }}
                    >
                      <td
                        style={{
                          color: theme ? textColorDarkMode : textColorLightMode,
                        }}
                      >
                        {pdf.name.split(".")[0]}
                      </td>
                      <td>
                        {pdf.rawDataDownload ? (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: theme
                                ? DownloadTextColorDarktMode
                                : DownloadTextColorLightMode,
                            }}
                            className={styles.pdfDownload}
                            href={pdf.rawDataDownload}
                            download
                          >
                            Download
                          </a>
                        ) : (
                          <span style={{ color: "gray" }}>N/A</span>
                        )}
                      </td>
                      <td>
                        {pdf.csvDownload ? (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: theme
                                ? DownloadTextColorDarktMode
                                : DownloadTextColorLightMode,
                            }}
                            className={styles.pdfDownload}
                            href={pdf.csvDownload}
                            download
                          >
                            Download
                          </a>
                        ) : (
                          <span style={{ color: "gray" }}>N/A</span>
                        )}
                      </td>
                      <td>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: theme
                              ? DownloadTextColorDarktMode
                              : DownloadTextColorLightMode,
                          }}
                          className={styles.pdfDownload}
                          href={pdf.download}
                          download
                        >
                          Download
                        </a>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </BoxContent>
    </div>
  );
}
